<template>
    <v-form ref="treatmentForm">
        <v-card>
            <v-card-title>{{ title }}</v-card-title>
            <v-card-text>
                <v-col cols="12">
                    <v-row no-gutters>
                        <v-col cols="2">
                            <v-text-field
                                label="Treatment #"
                                :readonly="!userCanEditNumber"
                                v-model="number"
                                type="number"
                                class="me-4"
                            />
                        </v-col>
                        <v-col cols="10">
                            <v-text-field
                                label="Treatment Name"
                                v-model="name"
                                :rules="requiredTextValueRules('Treatment Name')"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="6">
                            <v-autocomplete
                                label="Treatment Type"
                                v-if="treatmentFormConfig"
                                :items="treatmentFormConfig?.treatmentTypeOptions"
                                item-title="displayName"
                                item-value="id"
                                v-model="typeRegistryEntryId"
                                :rules="requiredSimpleValueRules('Treatment Type')"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="12">
                            <v-card-title>Actives</v-card-title>
                            <v-row v-for="active in actives" no-gutters v-if="treatmentFormConfig">
                                <v-col cols="12" v-if="selectedTreatmentComponent === active">
                                    <treatment-component-form
                                        :treatment-component="active"
                                        variant="inline"
                                        :form-config="treatmentFormConfig"
                                        @onSaved="updateActiveComponent"
                                        @onCancel="closeEditingTreatmentComponent"
                                    />
                                </v-col>
                                <v-col cols="7" v-else>
                                    <v-row
                                        no-gutters
                                        @mouseover="hoveredTreatmentComponent = active"
                                        @mouseleave="hoveredTreatmentComponent = undefined"
                                    >
                                        <treatment-component-chip
                                            :treatment-component="active"
                                            :registry-entries-for-lookups="treatmentFormConfig"
                                            @onClick="editTreatmentComponent(active)"
                                        />
                                        <v-col align-self="center" class="ms-n1">
                                            <v-icon-btn
                                                density="compact"
                                                v-if="hoveredTreatmentComponent == active"
                                                @click="deleteTreatmentComponent('active', active)"
                                            ><v-icon size="small" color="error" class="ma-0 pa-0"
                                            >$delete</v-icon
                                            ></v-icon-btn
                                            >
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col cols="12">
                                    <treatment-component-form
                                        v-if="selectedTypeForNewTreatmentComponent === 'active'"
                                        variant="inline"
                                        :form-config="treatmentFormConfig"
                                        @onSaved="saveTreatmentComponent"
                                        @onCancel="closeEditingTreatmentComponent"
                                    />
                                    <v-chip
                                        v-else
                                        variant="text"
                                        @click="addNewTreatmentComponent('active')"
                                        prepend-icon="$plus"
                                        class="ma-1"
                                    >
                                        Add Active
                                    </v-chip>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card-title>Inactives</v-card-title>
                            <v-row v-for="inactive in inactives" no-gutters v-if="treatmentFormConfig">
                                <v-col cols="12" v-if="selectedTreatmentComponent === inactive">
                                    <treatment-component-form
                                        :treatment-component="inactive"
                                        variant="inline"
                                        :form-config="treatmentFormConfig"
                                        @onSaved="updateInactiveComponent"
                                        @onCancel="closeEditingTreatmentComponent"
                                    />
                                </v-col>
                                <v-col cols="7" v-else>
                                    <v-row
                                        no-gutters
                                        @mouseover="hoveredTreatmentComponent = inactive"
                                        @mouseleave="hoveredTreatmentComponent = undefined"
                                    >
                                        <treatment-component-chip
                                            :treatment-component="inactive"
                                            :registry-entries-for-lookups="treatmentFormConfig"
                                            @onClick="editTreatmentComponent(inactive)"
                                        />
                                        <v-col align-self="center" class="ms-n1">
                                            <v-icon-btn
                                                density="compact"
                                                v-if="hoveredTreatmentComponent == inactive"
                                                @click="deleteTreatmentComponent('inactive', inactive)"
                                            ><v-icon size="small" color="error" class="ma-0 pa-0"
                                            >$delete</v-icon
                                            ></v-icon-btn
                                            >
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col cols="12">
                                    <treatment-component-form
                                        v-if="selectedTypeForNewTreatmentComponent === 'inactive'"
                                        variant="inline"
                                        :form-config="treatmentFormConfig"
                                        @onSaved="saveTreatmentComponent"
                                        @onCancel="closeEditingTreatmentComponent"
                                    />
                                    <v-chip
                                        v-else
                                        variant="text"
                                        @click="addNewTreatmentComponent('inactive')"
                                        prepend-icon="$plus"
                                        class="ma-1"
                                    >
                                        Add Inactive
                                    </v-chip>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-col>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn color="primary" @click="saveTreatment"> {{ submitButtonText }} </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script setup lang="ts">
import {
    TreatmentComponentSetupConfig,
    TreatmentComponentType,
    TreatmentFormConfig,
    treatmentsAPI,
    TreatmentSetupConfig,
} from "@/services/treatments/treatmentsAPI";
import { onMounted, ref, computed } from "vue";
import { requiredSimpleValueRules, requiredTextValueRules } from "@/lib/validationUtils";
import TreatmentComponentForm from "@/components/Treatments/TreatmentComponentForm.vue";
import TreatmentComponentChip from "@/components/Treatments/TreatmentComponentChip.vue";
import { withLoadingScreen } from "@/lib/loading";
import { EventBus } from "@/lib/event-bus";

const { experimentSetupStatus, formConfig, treatment, existingTreatmentsCount } = defineProps<{
    experimentSetupStatus: string;
    formConfig?: TreatmentFormConfig;
    treatment?: TreatmentSetupConfig;
    existingTreatmentsCount?: number;
}>();

const emit = defineEmits<{
    (e: "onSaved", treatmentSetupConfig: TreatmentSetupConfig, treatmentComponentIdsToDelete: string[]): void;
}>();

const treatmentForm = ref();

const number = ref<number>(calculateInitialNumberValue());
const name = ref<string | null>(treatment?.name ?? null);
const typeRegistryEntryId = ref<string | null>(treatment?.typeRegistryEntryId ?? null);
const actives = ref<TreatmentComponentSetupConfig[]>(treatment?.actives ?? []);
const inactives = ref<TreatmentComponentSetupConfig[]>(treatment?.inactives ?? []);

const treatmentComponentIdsToDelete = ref<string[]>([]);

const hoveredTreatmentComponent = ref<TreatmentComponentSetupConfig>();
const treatmentFormConfig = ref<TreatmentFormConfig | undefined>(formConfig ?? undefined);
const selectedTypeForNewTreatmentComponent = ref<TreatmentComponentType | undefined>(undefined);
const selectedTreatmentComponent = ref<TreatmentComponentSetupConfig | undefined>(undefined);

onMounted(async () => {
    if (!formConfig) {
        await withLoadingScreen(async () => {
            treatmentFormConfig.value = await treatmentsAPI.fetchTreatmentFormConfig();
        });
    }
});

const userCanEditNumber = computed(() => {
    return experimentSetupStatus !== "Final";
})

function addNewTreatmentComponent(type: TreatmentComponentType) {
    selectedTreatmentComponent.value = undefined;
    selectedTypeForNewTreatmentComponent.value = type;
}

function editTreatmentComponent(treatmentComponentSetupConfig: TreatmentComponentSetupConfig) {
    selectedTypeForNewTreatmentComponent.value = undefined;
    selectedTreatmentComponent.value = treatmentComponentSetupConfig;
}

function closeEditingTreatmentComponent() {
    selectedTypeForNewTreatmentComponent.value = undefined;
    selectedTreatmentComponent.value = undefined;
}

async function validate() {
    const form = treatmentForm.value;
    const validationResult = await form.validate();
    if (!validationResult.valid) {
        const errors = validationResult.errors;
        const msgs = errors.map((error) => {
            return error.errorMessages.join(", ");
        });
        EventBus.$emit("globalAlert", `There are validation errors: ${msgs.join(", ")}`);
        return false;
    } else {
        return true;
    }
}

async function saveTreatment() {
    if (!(await validate())) {
        return;
    }

    const treatmentToSave = {
        ...treatment,
        number: number.value,
        name: name.value!,
        typeRegistryEntryId: typeRegistryEntryId.value!,
        type: typeRegistryEntryId.value!,
        actives: actives.value,
        inactives: inactives.value,
    }
    emit("onSaved", treatmentToSave, treatmentComponentIdsToDelete.value);
}

function saveTreatmentComponent(treatmentComponentSetupConfig: TreatmentComponentSetupConfig) {
    switch (selectedTypeForNewTreatmentComponent.value) {
        case "active":
            actives.value.push(treatmentComponentSetupConfig);
            break;
        case "inactive":
            inactives.value.push(treatmentComponentSetupConfig);
            break;
    }
    closeEditingTreatmentComponent();
}

function updateTreatmentComponent(type: TreatmentComponentType, treatmentComponentSetupConfig: TreatmentComponentSetupConfig) {
    switch (type) {
        case "active": {
            const indexToUpdate = actives.value.indexOf(selectedTreatmentComponent.value!);
            if (indexToUpdate !== -1) {
                actives.value[indexToUpdate] = treatmentComponentSetupConfig;
            }
            break
        }
        case "inactive": {
            const indexToUpdate = inactives.value.indexOf(selectedTreatmentComponent.value!);
            if (indexToUpdate !== -1) {
                inactives.value[indexToUpdate] = treatmentComponentSetupConfig;
            }
            break;
        }
    }
    closeEditingTreatmentComponent();
}

function updateActiveComponent(treatmentComponentSetupConfig: TreatmentComponentSetupConfig) {
    updateTreatmentComponent("active", treatmentComponentSetupConfig);
}

function updateInactiveComponent(treatmentComponentSetupConfig: TreatmentComponentSetupConfig) {
    updateTreatmentComponent("inactive", treatmentComponentSetupConfig);
}

function deleteTreatmentComponent(
    type: TreatmentComponentType,
    treatmentComponentSetupConfig: TreatmentComponentSetupConfig
) {
    switch (type) {
        case "active":
            const indexOfActiveToDelete = actives.value.indexOf(treatmentComponentSetupConfig);
            actives.value.splice(indexOfActiveToDelete, 1);
            break;
        case "inactive":
            const indexOfInactiveToDelete = inactives.value.indexOf(treatmentComponentSetupConfig);
            inactives.value.splice(indexOfInactiveToDelete, 1);
            break;
    }
    if ("id" in treatmentComponentSetupConfig) {
        treatmentComponentIdsToDelete.value.push(treatmentComponentSetupConfig.id as string);
    }
    selectedTreatmentComponent.value = undefined;
}

function calculateInitialNumberValue(): number {
    if (treatment) {
        return treatment.number;
    } else if (existingTreatmentsCount) {
        return existingTreatmentsCount + 1;
    } else {
        return 1;
    }
}

const title = computed(() => {
    return treatment ? "Edit Treatment" : "Create Treatment";
})

const submitButtonText = computed(() => {
    return treatment ? "Save Treatment" : "Add Treatment";
})

</script>
